import { FAILURE_REASONS, FAILURE_REASON_META, type FailureInfo, type FailureReason } from '@1qa/shared';
import { Badge, type Tone } from '@/components/ui';
import type { TestResult } from '@/lib/types';
import { formatDuration } from '@/lib/format';
import { failureOf } from '@/lib/failures';

/** Why a web / mobile test failed (TestResult.failure, see @1qa/shared failures.ts). */

const TONE: Record<FailureReason, Tone> = {
  ELEMENT_NOT_FOUND: 'danger',
  ACTION_NOT_PERFORMED: 'warning',
  INVALID_TEST_DATA: 'info',
  ENVIRONMENT: 'muted',
};

export function FailureReasonBadge({ failure }: { failure: FailureInfo }) {
  const m = FAILURE_REASON_META[failure.reason];
  return (
    <Badge tone={TONE[failure.reason]} className="shrink-0">
      <span title={m.description}>{m.label}</span>
    </Badge>
  );
}

/** Reason, failing step, e2e's evidence and what to do next. */
export function FailurePanel({ failure, stepText }: { failure: FailureInfo; stepText?: string }) {
  const m = FAILURE_REASON_META[failure.reason];
  const facts = [
    ['Looked for', failure.locator],
    ['Expected', failure.expected],
    ['Seen on screen', failure.observed],
    ['Waited', failure.waitedMs !== undefined ? formatDuration(failure.waitedMs) : undefined],
  ].filter((f): f is [string, string] => !!f[1]);
  return (
    <div className="rounded-card border border-border bg-surface-sunken p-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold tracking-label text-fg-muted uppercase">Why it failed</span>
        <FailureReasonBadge failure={failure} />
        {failure.code && <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-[11px] text-fg-secondary">{failure.code}</code>}
      </div>
      <p className="mt-2 text-fg">{m.description}</p>
      {failure.stepIndex !== undefined && (
        <p className="mt-1 text-fg-secondary">
          At step {failure.stepIndex + 1}
          {stepText ? <>: <span className="text-fg">{stepText}</span></> : null}
        </p>
      )}
      {failure.message && <p className="mt-2 whitespace-pre-wrap text-fg-secondary">{failure.message}</p>}
      {facts.length > 0 && (
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
          {facts.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-fg-muted">{k}</dt>
              <dd className="font-mono break-words text-fg">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-3 border-t border-border-subtle pt-3 text-xs text-fg-secondary">
        <span className="font-semibold text-fg">What to do: </span>
        {m.hint}
      </p>
    </div>
  );
}

/** "Why tests failed" counts for a run; nothing when no result has a classified failure. */
export function FailureBreakdown({ results }: { results: TestResult[] }) {
  const counts = new Map<FailureReason, number>();
  let unclassified = 0;
  for (const r of results) {
    if (r.status !== 'FAILED' && r.status !== 'ERROR') continue;
    const f = failureOf(r);
    if (f) counts.set(f.reason, (counts.get(f.reason) ?? 0) + 1);
    else unclassified++;
  }
  if (counts.size === 0) return null;
  return (
    <div className="mt-5 border-t border-border-subtle pt-4">
      <div className="mb-2 text-xs font-semibold tracking-label text-fg-muted uppercase">Why tests failed</div>
      <ul className="flex flex-wrap gap-2">
        {FAILURE_REASONS.filter((r) => counts.has(r)).map((r) => (
          <li key={r} className="inline-flex items-center gap-2" title={FAILURE_REASON_META[r].description}>
            <Badge tone={TONE[r]}>
              <span className="tabular-nums">{counts.get(r)}</span> {FAILURE_REASON_META[r].label}
            </Badge>
          </li>
        ))}
        {unclassified > 0 && (
          <li>
            <Badge tone="neutral">
              <span className="tabular-nums">{unclassified}</span> Other
            </Badge>
          </li>
        )}
      </ul>
    </div>
  );
}
